// analytics/posthogClient.ts
// Lazy posthog-js loader: keeps the SDK out of the initial bundle. Calls made
// before it loads are queued and replayed in order once it is ready.
import type { PostHog } from 'posthog-js';

const MAX_QUEUE = 100;
const RESET_FLAG_KEY = 'posthog_pending_reset';

type Task = (ph: PostHog) => void;

const enabled =
  typeof window !== 'undefined' && !!process.env.NEXT_PUBLIC_POSTHOG_KEY;

let client: PostHog | null = null;
let loadPromise: Promise<void> | null = null;
let scheduled = false;
let queue: Task[] = [];

function readResetFlag() {
  try {
    return window.localStorage.getItem(RESET_FLAG_KEY) !== null;
  } catch {
    return false;
  }
}

function writeResetFlag(value: boolean) {
  try {
    if (value) window.localStorage.setItem(RESET_FLAG_KEY, '1');
    else window.localStorage.removeItem(RESET_FLAG_KEY);
  } catch {
    // storage unavailable: the in-memory queued reset still covers this page
  }
}

function runTask(task: Task, ph: PostHog) {
  try {
    task(ph);
  } catch (error) {
    console.error('PostHog task failed', error);
  }
}

export function loadPostHog(): Promise<void> {
  if (!enabled) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        autocapture: false,
        // Real-user page load metrics ($web_vitals); overrides the project setting.
        capture_performance: { web_vitals: true },
      });
      client = posthog;
      // A logout that reloaded the page before we loaded: reset before any identify.
      if (readResetFlag()) {
        posthog.reset();
        writeResetFlag(false);
      }
      const pending = queue;
      queue = [];
      pending.forEach((task) => runTask(task, posthog));
    })
    .catch((error) => {
      console.error('Failed to load posthog-js', error);
      queue = [];
    });
  return loadPromise;
}

// Loads after the page `load` event, then when the browser is idle.
export function schedulePostHogLoad() {
  if (!enabled || scheduled) return;
  scheduled = true;
  const run = () => {
    loadPostHog();
  };
  const whenIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 1);
    }
  };
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });
}

export function withPostHog(fn: Task) {
  if (!enabled) return;
  if (client) {
    runTask(fn, client);
    return;
  }
  queue.push(fn);
}

export function capturePostHog(
  event: string,
  properties?: Record<string, any>
) {
  // Only captures are dropped once the queue is full: identify and reset never are.
  if (!client && queue.length >= MAX_QUEUE) return;
  // Timestamp taken now so queued events keep their real time.
  const timestamp = new Date();
  withPostHog((ph) => ph.capture(event, properties, { timestamp }));
}

export function resetPostHog() {
  if (!enabled) return;
  if (client) {
    client.reset();
    return;
  }
  // goToLoginPage reloads the page, losing the queue: persist the reset too.
  writeResetFlag(true);
  withPostHog((ph) => ph.reset());
}
